import React, { ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import EnvironmentBanner, { EnvironmentBannerProps } from './EnvironmentBanner';
import NavMenuItem from './NavMenuItem';
import { VersionUpdateBanner } from './VersionUpdateBanner';
import { pureWhite, bentoSlate1 } from './colors';
import BentoLogoDark from '../public/bento-logo-dark.png';
import BentoLogoSmall from '../public/bento-logo-small.svg';
import BentoIcon from './BentoIcon';
import { getCookie } from './utilities';
import constants from './constants';

const drawerWidth = 176;
const appBarHeight = 56;

const LogoImg = styled('img')({ aspectRatio: 2.85, height: 50 });

export type AppNavigationSubLink = {
  title: string;
  route: string;
  showWarning?: boolean;
  id?: string;
};

export type AppNavigationLink = {
  title: string;
  id: string;
  route: string | string[];
  items?: AppNavigationSubLink[];
};

type AppLayoutProps = {
  id?: string;
  children: ReactNode;
  links: AppNavigationLink[];
  warnsFor?: boolean;
  hideLayout?: boolean;
  envProps?: EnvironmentBannerProps;
  header?: ReactNode;
  footer?: ReactNode;
};

export default function AppLayout({
  id,
  children,
  links,
  warnsFor,
  hideLayout,
  envProps,
  header,
  footer,
}: AppLayoutProps) {
  const hasAccess = getCookie(constants.COOKIES.ACCESS);
  const hasRefresh = getCookie(constants.COOKIES.REFRESH);

  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme(); // Get the theme
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // Use theme directly
  const handleDrawerToggle = () => {
    setMobileOpen((prevOpen) => !prevOpen);
  };

  if (hideLayout || (!hasAccess && !hasRefresh)) {
    return <>{children}</>;
  }

  const { debug, ...bannerProps } = envProps || {};

  const drawerContents = [
    !isSmallScreen && (
      <img src={BentoLogoDark} alt="Bento" width={160} height={56} key="logo-large" />
    ),
    <Box display={{ xs: 'flex', sm: 'none' }} key="logo-small">
      <LogoImg src={BentoLogoDark} alt="Bento" />
      <IconButton
        onClick={() => setMobileOpen(false)}
        sx={{ paddingLeft: 0.75, color: '#fff' }}
        size="large"
      >
        <ExpandLessIcon />
      </IconButton>
    </Box>,
    <List id={id} sx={{ padding: 0 }} key="nav-list">
      {links.map(({ title, id, route, items }) => {
        if (id === 'divider') {
          return <Divider key={id} sx={{ my: 2, borderColor: pureWhite }} />;
        }

        const isLinkActive = Array.isArray(route)
          ? route.includes(location.pathname)
          : location.pathname.startsWith(route);

        return (
          <NavMenuItem
            id={id}
            key={title}
            title={title}
            route={route}
            isActive={isLinkActive}
            items={items}
            showWarningFor={warnsFor}
            onClick={() => {
              if (mobileOpen) {
                setMobileOpen(false);
              }
            }}
          />
        );
      })}
    </List>,
    debug && <EnvironmentBanner {...bannerProps} key="env-banner" />,
  ];

  return (
    <>
      <VersionUpdateBanner />
      <Box display="flex">
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            height: `${appBarHeight}px`,
            paddingX: { xs: 0, sm: 1 },
            paddingY: 0,
          }}
        >
          <Box
            height="100%"
            display="flex"
            flexDirection="row"
            alignItems="center"
            paddingLeft={{ xs: 'none', sm: 20 }}
          >
            <Box display="flex" alignItems="center">
              <Box bgcolor="#202E39" display={{ xs: 'inline', sm: 'none' }}>
                <img src={BentoLogoSmall} alt="Bento" width={50} />
              </Box>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                size="large"
                sx={{ marginLeft: 0, display: { xs: 'block', sm: 'none' } }}
              >
                <BentoIcon name="bars" sx={{ fontSize: '1.8rem' }} />
              </IconButton>
            </Box>
            <Box width="100%">{header}</Box>
          </Box>
        </AppBar>
        <Drawer
          variant={isSmallScreen ? 'temporary' : 'permanent'}
          open={isSmallScreen ? mobileOpen : true}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 'inherit',
              padding: 0,
              backgroundColor: bentoSlate1,
              color: '#81cbf1',
              overflowX: 'hidden',
            },
          }}
        >
          {drawerContents}
        </Drawer>
        <Box
          component="main"
          flexGrow={1}
          maxWidth="100vw"
          minHeight="94vh"
          marginTop={`${appBarHeight}px`}
        >
          {children}
        </Box>
      </Box>
      {footer}
    </>
  );
}
